import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import CallPreview from "../CallPreview";
import MessageAvatar from "../MessageAvatar";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import notificationService from "../../services/notificationService";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "flex-end",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  },

  messageLeftContent: {
    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    marginLeft: 8,
    position: "relative",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    height: "auto",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
  },

  messageRightContent: {
    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    position: "relative",
    marginRight: 8,
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
    backgroundColor: "inherit",
    paddingLeft: 4,
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const CtwaPreview = ({ context }) => {
  if (!context) return null;
  let ctx;
  try {
    ctx = typeof context === 'string' ? JSON.parse(context) : context;
  } catch {
    return null;
  }
  if (!ctx.sourceUrl && !ctx.title && !ctx.description && !ctx.thumbnailUrl && !ctx.thumbnail) return null;
  // Always prefer base64 thumbnail if present
  const thumbSrc = ctx.thumbnail
    ? `data:image/jpeg;base64,${ctx.thumbnail}`
    : ctx.thumbnailUrl;
  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: 8,
      marginBottom: 6,
      padding: 8,
      background: '#f9f9f9',
      display: 'flex',
      alignItems: 'flex-start',
      maxWidth: 400
    }}>
      {thumbSrc && (
        <img
          src={thumbSrc}
          alt={ctx.title}
          style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginRight: 10 }}
        />
      )}
      <div style={{ flex: 1 }}>
        {ctx.title && <div style={{ fontWeight: 600, fontSize: 15 }}>{ctx.title}</div>}
        {ctx.description && <div style={{ color: '#555', fontSize: 13, margin: '4px 0' }}>{ctx.description}</div>}
        {ctx.sourceUrl && <a href={ctx.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontSize: 13, wordBreak: 'break-all' }}>{ctx.sourceUrl}</a>}
      </div>
    </div>
  );
};

const MessagesList = ({ ticketId, isGroup }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            // Save scroll position before loading more
            let messagesListDiv = document.getElementById("messagesList");
            let prevScrollHeight = messagesListDiv ? messagesListDiv.scrollHeight : 0;
            let prevScrollTop = messagesListDiv ? messagesListDiv.scrollTop : 0;

            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);

            // Log message order for debugging
            setTimeout(() => {
              const order = data.messages.map(m => m.createdAt);
              console.log("[MessagesList] Loaded page", pageNumber, "order:", order);
            }, 0);

            // Only scroll to bottom on first page
            if (pageNumber === 1 && data.messages.length > 1) {
              scrollToBottom();
            } else if (pageNumber > 1 && messagesListDiv) {
              // Maintain scroll position after prepending older messages
              setTimeout(() => {
                if (messagesListDiv) {
                  messagesListDiv.scrollTop = messagesListDiv.scrollHeight - prevScrollHeight + prevScrollTop;
                }
              }, 0);
            }
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    // Set current ticket for notification service
    notificationService.setCurrentTicket(ticketId);

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
        
        // Show notification for new message only if:
        // - Message is not from current user (unless system message)
        // - Tab is not visible or user is not viewing this ticket
        if (!data.message.fromMe || data.message.nameUser === 'SYSTEM' || data.message.nameUser === 'SISTEMA') {
          // Only notify if tab is hidden or user is viewing a different ticket
          if (document.visibilityState === 'hidden' || 
              !data.message.ticketId || 
              data.message.ticketId !== ticketId) {
            notificationService.handleNewMessage(data.message, data.ticket);
          }
        }
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    // Cleanup: reset current ticket when component unmounts
    return () => {
      notificationService.setCurrentTicket(null);
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    else if (message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") {
      return <CallPreview message={message} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
      else if (/^.*\.webp$/i.exec(message.mediaUrl) && message.mediaType === "image") {
        return (
          <div style={{ 
            background: 'transparent', 
            maxWidth: '200px',
            maxHeight: '200px',
            margin: '5px 0'
          }}>
            <img 
              src={message.mediaUrl} 
              alt="Sticker" 
              style={{ 
                width: '100%', 
                height: 'auto',
                objectFit: 'contain',
                background: 'transparent'
              }}
            />
          </div>
        );
      }
    else if ( /^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  // Helper function to check if two messages are from the same sender
  const isSameSender = (msg1, msg2) => {
    if (!msg1 || !msg2) return false;
    if (msg1.fromMe !== msg2.fromMe) return false;
    
    if (msg1.fromMe) {
      // For outgoing messages, compare nameUser
      return msg1.nameUser === msg2.nameUser;
    } else {
      // For incoming messages, compare contactId
      return msg1.contactId === msg2.contactId;
    }
  };

  // Helper function to check if message should show avatar
  // Show avatar only on the last message of a continuous set from the same sender
  const shouldShowAvatar = (message, index) => {
    // Always show avatar for special message types (stickers, calls)
    if (message.mediaType === "image" && /^.*\.webp$/i.exec(message.mediaUrl)) {
      return true;
    }
    if (message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") {
      return true;
    }
    
    // Check if next message is from different sender or doesn't exist
    const nextMessage = messagesList[index + 1];
    if (!nextMessage) {
      // This is the last message, show avatar
      return true;
    }
    
    // If next message is from different sender, show avatar
    if (!isSameSender(message, nextMessage)) {
      return true;
    }
    
    // If next message is a special type, show avatar
    if (nextMessage.mediaType === "image" && /^.*\.webp$/i.exec(nextMessage.mediaUrl)) {
      return true;
    }
    if (nextMessage.mediaType === "call_received" || nextMessage.mediaType === "call_accepted" || nextMessage.mediaType === "call_rejected") {
      return true;
    }
    
    // Next message is from same sender and is regular message, don't show avatar
    return false;
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        // Special handling for WebP stickers - render them outside the message bubble
        if (message.mediaType === "image" && /^.*\.webp$/i.exec(message.mediaUrl)) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div 
                style={{ 
                  alignSelf: message.fromMe ? 'flex-end' : 'flex-start',
                  position: 'relative',
                  marginLeft: message.fromMe ? '20px' : '0',
                  marginRight: !message.fromMe ? '20px' : '0',
                  marginTop: '2px'
                }}
              >
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.fromMe ? 'flex-end' : 'flex-start'
                }}>
                  <img 
                    src={message.mediaUrl} 
                    alt="Sticker" 
                    style={{ 
                      maxWidth: '150px',
                      maxHeight: '150px',
                      objectFit: 'contain',
                      background: 'transparent'
                    }}
                  />
                  <span 
                    style={{
                      fontSize: '11px',
                      color: '#999',
                      marginTop: '2px',
                      marginRight: message.fromMe ? '5px' : '0',
                      marginLeft: !message.fromMe ? '5px' : '0'
                    }}
                  >
                    {format(parseISO(message.createdAt), "h:mm a")}
                    {message.fromMe && renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        // Special handling for call messages - render them outside the message bubble
        if (message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              {checkMessageMedia(message)}
            </React.Fragment>
          );
        }

        // Regular message handling for non-stickers
        if (!message.fromMe) {
          const showAvatar = shouldShowAvatar(message, index);
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageLeft}>
                {showAvatar && <MessageAvatar message={message} size={32} />}
                {!showAvatar && <div style={{ width: 32, height: 32, flexShrink: 0 }} />}
                <div className={classes.messageLeftContent}>
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                  {isGroup && (
                    <span className={classes.messageContactName}>
                      {message.contact?.name}
                    </span>
                  )}
                  {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") && 
                    !(message.mediaType === "image" && /^.*\.webp$/i.exec(message.mediaUrl)) && 
                    !(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") &&
                    checkMessageMedia(message)}
                  <div className={classes.textContentItem}>
                    {message.quotedMsg && renderQuotedMessage(message)}
                    {message.ctwaContext && <CtwaPreview context={message.ctwaContext} />}
                    {!(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") && 
                     !(message.mediaType === "image" || message.mediaType === "video" || message.mediaType === "audio") && (
                      <MarkdownWrapper>{message.body}</MarkdownWrapper>
                    )}
                    <span className={classes.timestamp}>
                      {format(parseISO(message.createdAt), "h:mm a")}
                    </span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          const showAvatar = shouldShowAvatar(message, index);
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageRight}>
                <div className={classes.messageRightContent}>
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                  {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard") && 
                    !(message.mediaType === "image" && /^.*\.webp$/i.exec(message.mediaUrl)) && 
                    !(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") &&
                    checkMessageMedia(message)}
                  <div
                    className={clsx(classes.textContentItem, {
                      [classes.textContentItemDeleted]: message.isDeleted,
                    })}
                  >
                    {message.isDeleted && (
                      <Block
                        color="disabled"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    )}
                    {message.quotedMsg && renderQuotedMessage(message)}
                    {message.ctwaContext && <CtwaPreview context={message.ctwaContext} />}
                    {!(message.mediaType === "call_received" || message.mediaType === "call_accepted" || message.mediaType === "call_rejected") && 
                     !(message.mediaType === "image" || message.mediaType === "video" || message.mediaType === "audio") && (
                      <MarkdownWrapper>{message.body}</MarkdownWrapper>
                    )}
                    <span className={classes.timestamp}>
                      {format(parseISO(message.createdAt), "h:mm a")}
                      {renderMessageAck(message)}
                    </span>
                  </div>
                </div>
                {showAvatar && <MessageAvatar message={message} size={32} />}
                {!showAvatar && <div style={{ width: 32, height: 32, flexShrink: 0 }} />}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;